import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { CustomReportsApiService } from 'src/services/customReportsApi'
import {
  CustomReportErrorResponse,
  CustomReportPayload,
} from 'src/services/customReportsApi/interfaces/customReports.interface'

const readError = (
  error: AxiosError<CustomReportErrorResponse>,
  fallback: string
) =>
  error.response?.data?.errors?.join(', ') ||
  error.response?.data?.error ||
  fallback

// Both the list and the summary counts move whenever a report is created,
// retried or deleted, so every mutation refreshes the pair.
const useInvalidateReports = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_CUSTOM_REPORTS] })
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_CUSTOM_REPORTS_SUMMARY],
    })
  }
}

interface UseCreateCustomReportMutationParams {
  onError?: () => void
  onSuccess?: () => void
}

export const useCreateCustomReportMutation = ({
  onError,
  onSuccess,
}: UseCreateCustomReportMutationParams = {}) => {
  const invalidate = useInvalidateReports()

  return useMutation({
    mutationFn: (payload: CustomReportPayload) =>
      CustomReportsApiService.create(payload),
    // No toast here: the caller shows the result modal, and two notifications
    // for one action is noise.
    onError: (error: AxiosError<CustomReportErrorResponse>) => {
      onError?.()
      toast.error(readError(error, 'Failed to start report generation'))
    },
    onSuccess: () => {
      invalidate()
      onSuccess?.()
    },
  })
}

export const useRetryCustomReportMutation = () => {
  const invalidate = useInvalidateReports()

  return useMutation({
    mutationFn: (reportId: string) => CustomReportsApiService.retry(reportId),
    onError: (error: AxiosError<CustomReportErrorResponse>) => {
      toast.error(readError(error, 'Failed to retry report generation'))
    },
    onSuccess: invalidate,
  })
}

export const useDeleteCustomReportMutation = () => {
  const invalidate = useInvalidateReports()

  return useMutation({
    mutationFn: (reportId: string) => CustomReportsApiService.remove(reportId),
    onError: (error: AxiosError<CustomReportErrorResponse>) => {
      toast.error(readError(error, 'Failed to delete report'))
    },
    onSuccess: () => {
      invalidate()
      toast.success('Report deleted')
    },
  })
}
